/**
 * The calendar's toolbar — `docs/activity.md` §6.1 (Board 2): **Today**, ‹ ›,
 * the range (never wrapping; in Day followed by that day's forecast), then on
 * the right **Side by side · Combined** when several people are shown and
 * **Day · Work week · Week · Month**.
 */
import type { ReactNode } from 'react'
import { CALENDAR_VIEW_LABELS, type ActivityView, type DayForecast } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { Forecast } from './blocks.js'
import { FREE_HATCH } from './look.js'

export function Segmented<T extends string>({
  options, value, onChange, size = 26, label,
}: {
  options: readonly { value: T; label: ReactNode }[]
  value: T
  onChange: (value: T) => void
  size?: 26 | 30 | 34
  label: string
}) {
  return (
    <span role="radiogroup" aria-label={label} className="inline-flex shrink-0 rounded-[10px] bg-line-soft p-[3px]">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn(
            'inline-flex items-center justify-center gap-[5px] whitespace-nowrap rounded-lg font-semibold',
            size === 26 ? 'px-[11px] text-[12.5px]' : size === 30 ? 'px-3 text-[13px]' : 'px-2.5 text-[13px]',
            option.value === value ? 'bg-surface text-text shadow-[0_1px_2px_rgba(20,20,10,.12)]' : 'text-text-muted hover:text-text',
          )}
          style={{ height: size }}
        >
          {option.label}
        </button>
      ))}
    </span>
  )
}

const square = 'inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-line bg-surface text-[12.5px] font-semibold text-text hover:bg-hover'

export function CalendarToolbar({
  range, view, views, onView, onToday, onStep, several, onSeveral, forecast, freeCount, height = 32,
}: {
  range: string
  view: ActivityView
  views: readonly ActivityView[]
  onView: (view: ActivityView) => void
  onToday: () => void
  onStep: (direction: 1 | -1) => void
  several: 'side' | 'combined' | null
  onSeveral: (value: 'side' | 'combined') => void
  forecast?: DayForecast | null
  /** Combined: *Free for all <n>* (§6.4.2). */
  freeCount?: number | null
  height?: 32 | 40
}) {
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={onToday} className={cn(square, 'px-3.5', height === 40 && 'text-[13px]')} style={{ height }}>Today</button>
      <button type="button" aria-label="Back" onClick={() => onStep(-1)} className={square} style={{ height, width: height }}>
        <Glyph name="chevronLeft" size={16} />
      </button>
      <button type="button" aria-label="Forward" onClick={() => onStep(1)} className={square} style={{ height, width: height }}>
        <Glyph name="chevronRight" size={16} />
      </button>
      <b className="ml-1.5 whitespace-nowrap text-[15px]">{range}</b>
      {forecast && (
        <span className="ml-1">
          {/* Board 3: the desktop's Day reads its forecast at 12, the sky at 15, the drop at 11. */}
          <Forecast weather={forecast} textSize={height === 40 ? 12.5 : 12} iconSize={height === 40 ? 14 : 15}
            dropSize={height === 40 ? 10 : 11} />
        </span>
      )}
      <span className="flex-1" />
      {/* Board 18 A: the key sits on the right, before *Side by side · Combined*. */}
      {freeCount ? (
        <span className="mr-1.5 inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-accent-dark">
          <span className="inline-block h-3 w-4 rounded border border-dashed border-accent-line" style={{ background: FREE_HATCH }} />
          Free for all {freeCount}
        </span>
      ) : null}
      {several && (
        <Segmented label="Several people" value={several} onChange={onSeveral} size={height === 40 ? 34 : 26}
          options={[{ value: 'side', label: 'Side by side' }, { value: 'combined', label: 'Combined' }]} />
      )}
      <Segmented label="View" value={view} onChange={onView} size={height === 40 ? 34 : 26}
        options={views.map((value) => ({ value, label: CALENDAR_VIEW_LABELS[value] }))} />
    </div>
  )
}
